type Orientation = 'horizontal' | 'vertical'
import { Favorite, MoreVertIcon } from '@tunasong/icons'
import { capitalize, dayjs, isFacade, isPersistedEntity } from '@tunasong/models'
import type { RenderMenuItemProps } from '@tunasong/plugin-lib'
import { isEntity, isPersisted } from '@tunasong/schemas'
import type { FC, MouseEvent } from 'react'
import React, { useCallback, useState } from 'react'
import { useDeviceInfo } from '../browser/device.hook.js'
import { useUIExtensions } from '../extensions.hook.js'
import { useMergeRefs } from '../hooks/merge-refs.js'
import { TunaEntityBreadcrumbs } from '../navigation/entity-breadcrumbs.js'
import { UserAvatar } from '../profile/index.js'
import { TagList } from '../tags/tag-list.js'
import { useCurrentUser } from '@tunasong/redux'
import { EntityIcon } from './entity-icon.js'
import { EntityListItemFacade } from './entity-list-item-facade.js'
import { StyledEntityAvatar, StyledListItemAvatar, StyledListItemText } from './list-item.styles.js'
import { usePreloadOnHover } from './preload-on-hover.js'
import { IconButton, ListItemIcon, ListItem, type ListItemProps, ListItemButton } from '../primitives/index.js'
import styles from './entity-list-item.module.css'

/** Pick only the ListItem-specific props to avoid HTMLAttributes<HTMLLIElement> vs HTMLAttributes<HTMLDivElement> conflicts with RenderMenuItemProps */
type ListItemOwnProps = Pick<
  ListItemProps,
  'dense' | 'button' | 'selected' | 'disabled' | 'disablePadding' | 'disableGutters' | 'secondaryAction' | 'alignItems'
>

export interface EntityListItemProps extends RenderMenuItemProps, ListItemOwnProps {
  /** Mark the item as a favorite */
  favorite?: boolean
  size?: 'tiny' | 'small' | 'large' | 'medium'
  hideSubtitle?: boolean
  orientation?: Orientation
  iconStyle?: 'avatar' | 'icon'
  showBreadcrumbs?: boolean | 'inactiveLinks'
  ref?: React.Ref<HTMLLIElement>
  onClick?(ev: MouseEvent): void
}

export const EntityListItem: FC<EntityListItemProps> = props => {
  const {
    className,
    favorite,
    orientation = 'horizontal',
    element,
    size,
    hideSubtitle = false,
    iconStyle = 'icon',
    actions = true,
    showBreadcrumbs = false,
    onClick,
    ref,
    ...restProps
  } = props

  const me = useCurrentUser()

  const { isMobile } = useDeviceInfo()

  // If we have a plugin, we can show the item enabled

  const disabled = props.disabled // || !hasPlugin

  const [localRef, setLocalRef] = useState<HTMLLIElement | null>(null)
  const mergedRef = useMergeRefs(setLocalRef, ref)

  usePreloadOnHover({ entityId: element?.id, containerEl: localRef })

  const uiExtensions = useUIExtensions()

  const label = element.name
  const hasExternalOwner = isPersisted(element) ? element.userId !== me.userId : false
  const subtitle =
    isPersistedEntity(element) && !hideSubtitle ? (
      <>
        {capitalize(element.type)}. Updated: {dayjs(element.updatedAt).fromNow()}
        {element.description ? ' - ' + element.description : ''}
        <TagList style={{ display: 'block', marginLeft: 8 }} tags={element.tags} size="small" />
      </>
    ) : null

  /** Actions */
  const [menuAnchorEl, setAnchorEl] = React.useState<Element | null>(null)
  const handleActionsClose = useCallback(() => setAnchorEl(null), [])
  const handleActions = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    ev.stopPropagation()
    setAnchorEl(el => (Boolean(el) ? null : ev.currentTarget))
  }, [])

  const title = disabled ? `No plugin for ${element.type}` : undefined

  const showMenuAction = !isMobile && isPersisted(element)

  const handleContext = useCallback((ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    setAnchorEl(el => (Boolean(el) ? null : ev.currentTarget))
  }, [])

  if (!(isEntity(element) && isPersisted(element))) {
    return null
  }

  const rightPadding = isMobile ? 0 : hasExternalOwner ? 12 : 8

  return isFacade(element) ? (
    <EntityListItemFacade
      className={className}
      element={element}
      {...restProps}
      ref={mergedRef as React.Ref<HTMLDivElement>}
      /** Override disabled if we have no plugin here */
      disabled={disabled}
    />
  ) : (
    <ListItem
      {...(restProps as Omit<typeof restProps, keyof React.DOMAttributes<HTMLDivElement>>)}
      ref={mergedRef as React.Ref<HTMLLIElement>}
      tabIndex={0}
      className={className}
      disabled={disabled}
      style={{
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      title={title}
      aria-orientation={orientation}
      onClick={disabled ? undefined : (onClick as React.MouseEventHandler<HTMLLIElement>)}
      onContextMenu={handleContext as React.MouseEventHandler<HTMLLIElement>}
      secondaryAction={
        <div className={styles.secondaryAction}>
          {favorite ? (
            <span title="You have this as a favorite">
              <Favorite color="var(--tuna-palette-error-main)" />
            </span>
          ) : null}
          {actions && !disabled ? (
            <>
              {hasExternalOwner && <UserAvatar userId={element.userId} size="tiny" />}
              {showMenuAction ? (
                <>
                  <IconButton tabIndex={-1} color="inherit" aria-label="settings" onClick={handleActions}>
                    <MoreVertIcon />
                  </IconButton>
                </>
              ) : null}
              {/* Expensive component so we run it on demand */}
              {menuAnchorEl && uiExtensions.entityList?.ItemMenu ? (
                <uiExtensions.entityList.ItemMenu
                  entity={element}
                  // clickAway will close the context menu on mobile
                  clickAway={!isMobile}
                  anchorEl={menuAnchorEl}
                  onClose={handleActionsClose}
                />
              ) : null}
            </>
          ) : null}
        </div>
      }
    >
      <ListItemButton disableRipple={true} className={styles.listItemButton}>
        {iconStyle === 'avatar' ? (
          <StyledListItemAvatar size={size}>
            <StyledEntityAvatar type={element.type} size={size} />
          </StyledListItemAvatar>
        ) : null}
        {iconStyle === 'icon' ? (
          <ListItemIcon
            style={{
              justifyContent: orientation === 'vertical' ? 'center' : undefined,
            }}
          >
            <EntityIcon entity={element} />
          </ListItemIcon>
        ) : null}

        <StyledListItemText
          style={{
            paddingRight: orientation === 'vertical' ? 0 : rightPadding,
          }}
          primary={
            <>
              {label}
              {showBreadcrumbs ? (
                <TunaEntityBreadcrumbs
                  entity={element}
                  showHome={false}
                  activeLinks={showBreadcrumbs === 'inactiveLinks' ? false : showBreadcrumbs}
                />
              ) : null}
            </>
          }
          secondary={subtitle}
        />
      </ListItemButton>
    </ListItem>
  )
}
